import {
  createBlockId,
  EXAM_BLOCK_LABELS,
  GAME_MODE_CATALOG,
  validateExam,
  type EvaluationBlock,
  type EvaluationExam,
  type GameMode,
} from '@interactive-room';

import { emptyPayloadForMode } from '../emptyPayloads';
import { generateSubjectAwareActivity } from '../studioActivityGeneration';
import { createExamId, saveEvaluationExam } from '../examStorage';
import type { ExamLevelId } from '../constants/examAiParams';
import type { RecipeDifficultyId } from '../constants/recipeAiParams';

export interface MultiToolActivityParams {
  subjectId: string;
  subjectTag: string;
  modes: GameMode[];
  momentCount: number;
  level: ExamLevelId;
  difficulty?: RecipeDifficultyId;
  tuning?: string;
  courseId?: string;
}

export interface MultiToolActivityResult {
  exam: EvaluationExam;
  notice?: string;
}

/** Reparte los modos elegidos en N momentos (round-robin). */
export function distributeModesAcrossMoments(modes: GameMode[], momentCount: number): GameMode[] {
  if (modes.length === 0 || momentCount < 1) return [];
  const count = Math.min(Math.max(momentCount, 1), 12);
  const sequence: GameMode[] = [];
  for (let i = 0; i < count; i += 1) {
    sequence.push(modes[i % modes.length]!);
  }
  return sequence;
}

/**
 * Genera una clase interactiva: un bloque por momento, cada uno con su herramienta.
 * Usa IA por bloque; si falla, cae a payload vacío del modo.
 */
export async function generateMultiToolInteractiveClass(
  params: MultiToolActivityParams,
): Promise<MultiToolActivityResult> {
  const modes = [...new Set(params.modes)];
  if (modes.length === 0) throw new Error('Selecciona al menos una herramienta');

  const sequence = distributeModesAcrossMoments(modes, params.momentCount);
  const notices: string[] = [];
  const now = Date.now();
  const titleSeed =
    params.tuning?.trim().replace(/\s+/g, ' ').slice(0, 64) ||
    `Clase · ${sequence.length} momentos`;

  const blocks: EvaluationBlock[] = [];
  for (let index = 0; index < sequence.length; index += 1) {
    const mode = sequence[index]!;
    const momentTuning = [
      `Momento ${index + 1} de ${sequence.length} de una clase multi-herramienta.`,
      `Herramientas del kit: ${modes.map((m) => GAME_MODE_CATALOG[m]?.displayName ?? m).join(', ')}.`,
      params.tuning?.trim() || 'Secuencia coherente para una misma clase.',
    ].join(' ');

    let payload: unknown = emptyPayloadForMode(mode);

    try {
      const generated = await generateSubjectAwareActivity({
        mode,
        subjectId: params.subjectId,
        subjectTag: params.subjectTag,
        level: params.level,
        difficulty: params.difficulty ?? 'medium',
        tuning: momentTuning,
        courseId: params.courseId,
      });
      payload = generated.payload;
      if (generated.notice) notices.push(`Momento ${index + 1}: ${generated.notice}`);
    } catch {
      notices.push(
        `Momento ${index + 1} (${GAME_MODE_CATALOG[mode]?.displayName ?? mode}): se usó plantilla local.`,
      );
    }

    blocks.push({
      id: createBlockId(),
      index,
      mode,
      label: EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode]?.displayName ?? mode,
      payload: payload as EvaluationBlock['payload'],
    });
  }

  const raw: EvaluationExam = {
    id: createExamId(),
    teacherId: 'teacher',
    title: titleSeed,
    subjectTag: params.subjectTag,
    courseId: params.courseId,
    resourceType: 'exam',
    deliveryFormat: 'interactive',
    deliveryProfile: 'interactive_class',
    createdAt: now,
    updatedAt: now,
    settings: {
      shuffleBlocks: false,
      allowRejoin: true,
      integrityMode: 'FLEXIBLE',
      advanceMode: 'HOST',
    },
    blocks,
  };

  const exam = saveEvaluationExam(validateExam(raw));
  return {
    exam,
    notice: notices.length > 0 ? notices.join(' ') : undefined,
  };
}
